import { useEffect, useRef, useCallback } from "react";

export const useInfiniteScroll = (loadMore, hasMore = true, threshold = 0.3) => {
  const observerRef = useRef(null);
  const observerInstanceRef = useRef(null);
  const hasMoreRef = useRef(hasMore);
  const loadMoreRef = useRef(loadMore);

  // Keep refs in sync with latest props — no stale closures
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  const reconnect = useCallback(() => {
    // Disconnect existing
    if (observerInstanceRef.current) {
      observerInstanceRef.current.disconnect();
      observerInstanceRef.current = null;
    }

    // Always read from ref so we never have stale closure values
    if (!observerRef.current || !hasMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current) {
          // Disconnect immediately to prevent double-fire
          observer.disconnect();
          observerInstanceRef.current = null;
          loadMoreRef.current();

          // Reconnect after load completes (300ms timeout inside loadMore + buffer)
          setTimeout(() => {
            reconnect();
          }, 350);
        }
      },
      { threshold, rootMargin: "200px" },
    );

    observerInstanceRef.current = observer;
    observer.observe(observerRef.current);
  }, [threshold]); // stable — reads hasMore/loadMore via refs

  useEffect(() => {
    if (!hasMore) {
      // Clean up when no more data
      if (observerInstanceRef.current) {
        observerInstanceRef.current.disconnect();
        observerInstanceRef.current = null;
      }
      return;
    }
    reconnect();
    return () => {
      if (observerInstanceRef.current) {
        observerInstanceRef.current.disconnect();
        observerInstanceRef.current = null;
      }
    };
  }, [hasMore, reconnect]);

  return observerRef;
};